<template>
  <PreviewBase
    v-if="type === 'approvalFlow'"
    :loading="false"
  >
    <div
      v-if="flow"
      class="approvals-library-preview d-flex flex-column ga-4"
    >
      <section class="approvals-library-preview-header d-flex flex-column ga-1">
        <ApprovalStatusPill
          :status="flow.status"
          mode="overall"
        />
        <h1 class="text-h1 text-textDarkest mb-1">
          {{ flow.draft_profile?.title || '' }}
        </h1>
        <p
          v-if="flow.draft_profile?.job_code"
          class="text-body-1 text-textLight mb-0"
        >
          Job Code: {{ flow.draft_profile.job_code }}
        </p>
      </section>

      <dl class="approvals-library-preview-metadata mb-0">
        <template
          v-for="row in metadataRows"
          :key="row.label"
        >
          <dt class="text-h3 text-textDark">
            {{ row.label }}
          </dt>
          <dd class="text-body-2 text-textLight mb-0">
            {{ row.value || '—' }}
          </dd>
        </template>
      </dl>

      <PreviewAlertCard
        v-if="flow.status === 'pending'"
        color="yellow"
        icon="mdi-dots-horizontal-circle-outline"
        title="Pending Workflow"
      >
        This job profile is in an active approval workflow.
        <template #footer>
          <div class="d-flex align-end ga-4 flex-wrap">
            <DpplNewButton
              color="primary"
              height="32"
              class="text-button-3"
              :loading="isRemindingFlow"
              @click="handleRemindPendingApprovers"
            >
              Remind Pending Approvers
            </DpplNewButton>
            <DpplNewButton
              variant="outlined"
              height="32"
              class="text-button-3 cancel-workflow-btn"
              :loading="isCancellingFlow"
              @click="handleCancelClick"
            >
              Cancel Workflow
            </DpplNewButton>
          </div>
        </template>
      </PreviewAlertCard>

      <section class="approvals-library-preview-stages d-flex flex-column ga-4">
        <h2 class="text-h2 text-textDarkest mb-0">
          Approval Stages
        </h2>

        <div
          v-for="stage in flow.stages"
          :key="stage.stage_number"
          class="d-flex flex-column ga-3"
        >
          <div class="text-h2 text-textDarkest">
            Stage {{ stage.stage_number }}: {{ stage.name }}
          </div>

          <ApprovalFlowSlot
            v-for="slot in stage.slots"
            :key="slot.ulid"
            :flow-slot="slot"
            mode="view"
          >
            <template
              v-if="showSlotActions(slot)"
              #actions
            >
              <ApprovalsLibraryPreviewSlotMenu
                :slot-data="slot"
                :draft-job-profile-ulid="flow.draft_job_profile_ulid"
                :flow-ulid="flow.ulid"
              />
            </template>
          </ApprovalFlowSlot>
        </div>
      </section>
    </div>

    <div
      v-else
      class="text-body-2 text-textLight"
    >
      Approval flow not found in the current dashboard view.
    </div>

    <CancelApprovalFlowModal
      :show="showCancelModal"
      :job-profile-title="flow?.draft_profile?.title || ''"
      :loading="isCancellingFlow"
      @close="showCancelModal = false"
      @cancel="handleCancelWorkflow"
    />
  </PreviewBase>
</template>

<script setup>
import {
  computed,
  ref,
  watch,
} from 'vue';
import { storeToRefs } from 'pinia';
import { useRoute } from 'vue-router';
import PreviewBase from '@/components/shared/PreviewBase.vue';
import PreviewAlertCard from '@/components/shared/PreviewAlertCard.vue';
import ApprovalFlowSlot from '@/components/jobProfiles/editor/approvals/ApprovalFlowSlot.vue';
import ApprovalStatusPill from '@/components/jobProfiles/editor/ApprovalStatusPill.vue';
import ApprovalsLibraryPreviewSlotMenu from '@/components/library/approvals/ApprovalsLibraryPreviewSlotMenu.vue';
import CancelApprovalFlowModal from '@/components/jobProfiles/editor/approvals/CancelApprovalFlowModal.vue';
import DpplNewButton from '@/components/datapeople/DpplNewButton.vue';
import { useApprovalsDashboardStore } from '@/stores/jobProfiles/approvalsDashboard';
import { fetchApprovalTemplates } from '@/api/approvalFlows';
import { formatApprovalFlowDate, getTemplateNameByUlid } from '@/helpers/approvalFlows';
import { formatUserDisplayName } from '@/helpers/user';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { useTracking } from '@/stores/tracking/tracking';

const route = useRoute();
const { addErrorSnackbar } = useAppSnackbar();

const {
  getFlowByUlid,
  remindFlowApprovers,
  cancelFlow,
} = useApprovalsDashboardStore();
const {
  remindingFlowUlid,
  cancellingFlowUlid,
} = storeToRefs(useApprovalsDashboardStore());

const templates = ref([]);
const showCancelModal = ref(false);

const type = computed(() => route.query?.type);
const previewUlid = computed(() => route.query?.preview);

const flow = computed(() => (
  previewUlid.value ? getFlowByUlid(previewUlid.value) : null
));

const templateName = computed(() => (
  getTemplateNameByUlid(templates.value, flow.value?.template_ulid)
));

const stagesLabel = computed(() => {
  const current = flow.value?.current_stage;
  const total = flow.value?.stage_count;

  if (current == null || total == null) return '';

  return `${current} / ${total}`;
});

const metadataRows = computed(() => [
  { label: 'Flow Template', value: templateName.value },
  { label: 'Stages', value: stagesLabel.value },
  { label: 'Created on', value: formatApprovalFlowDate(flow.value?.created_at) },
  { label: 'Created by', value: formatUserDisplayName(flow.value?.created_by) },
]);

const isRemindingFlow = computed(() => (
  remindingFlowUlid.value === flow.value?.ulid
));

const isCancellingFlow = computed(() => (
  cancellingFlowUlid.value === flow.value?.ulid
));

const showSlotActions = (slot) => {
  if (flow.value?.status !== 'pending') {
    return false;
  }

  const status = String(slot?.status || '').toLowerCase();

  return status === 'pending' || status === 'staged';
};

const loadTemplates = async () => {
  try {
    const data = await fetchApprovalTemplates();
    templates.value = data.templates ?? [];
  } catch (error) {
    addErrorSnackbar({
      text: `Failed to load approval templates — ${error.message}`,
    });

    throw error;
  }
};

const handleRemindPendingApprovers = async () => {
  if (!flow.value) return;

  useTracking().approvalLibClickedRemind();

  await remindFlowApprovers(flow.value);
};

const handleCancelClick = () => {
  useTracking().approvalLibClickedCancel();

  showCancelModal.value = true;
};

const handleCancelWorkflow = async () => {
  if (!flow.value?.ulid) return;

  await cancelFlow(flow.value.ulid);

  useTracking().approvalPanelCancelledFlow({ location: 'Dashboard Preview' });

  showCancelModal.value = false;
};

watch(
  [previewUlid, type],
  () => {
    if (type.value === 'approvalFlow' && previewUlid.value) {
      loadTemplates();
    }
  },
  { immediate: true },
);
</script>

<style scoped lang="less">
.approvals-library-preview-metadata {
  display: grid;
  grid-template-columns: 110px 1fr;
  gap: 4px;
  align-items: start;
}

:deep(.cancel-workflow-btn.v-btn) {
  background: @white;
  color: @red-700;
  border-color: @stroke-default;
}

:deep(.approval-flow-slot) {
  width: 100%;
  margin-right: -24px;
  padding-right: 12px;
  padding-top: 8px;
  padding-bottom: 8px;

  .approval-flow-slot-inner {
    gap: 8px;
  }

  .slot-actions {
    opacity: 0;
    transition: opacity 0.1s;
  }

  &:hover {
    background: @bg-canvas;

    .slot-actions {
      opacity: 1;
    }
  }
}
</style>
